import type { MicrositeDetail } from '../types/api.types';
import type {
    MicrositeFormData,
    HeroImage,
    HeroTag,
    GalleryImage,
    DealItem,
    TimingSlot,
} from '../pages/CreateMicrosite/types/microsite.types';

/**
 * Transforms API response data (MicrositeDetail) into form data (MicrositeFormData)
 * for populating the CreateMicrosite form in edit mode.
 *
 * Handles conversion of:
 * - Image URLs (existing images on server)
 * - Date strings to Date objects
 * - Nested API structure to flat form structure
 * - Optional fields
 */
export const transformApiToFormData = (apiData: MicrositeDetail): MicrositeFormData => {
    const formData: MicrositeFormData = {
        // Basic Info
        micrositeID: apiData._id, // Extract microsite ID for updates
        micrositeType: apiData.store ? 'store' : 'brand',
        storeBrandID: apiData.storeBrand._id,
        storeBrandSlug: apiData.storeBrandSlug,
        storeBrandName: apiData.storeBrand.name, // Store display name for edit mode
        storeID: apiData.store?._id,
        storeSlug: apiData.storeSlug,
        storeName: apiData.store?.name, // Store display name for edit mode

        // Branding
        tagline: apiData.tagline,
        logo: apiData.logo, // URL string (existing image)

        // Meta Data
        metaData: apiData.metaData
            ? {
                  title: apiData.metaData.title,
                  description: apiData.metaData.description,
                  keywords: apiData.metaData.keywords?.[0]?.split(',').map((k) => k.trim()) || [],
                  ogImage: apiData.metaData.ogImage, // URL string (existing image)
              }
            : undefined,

        // Hero Section
        hero: apiData.hero
            ? {
                  imageUrls: (apiData.hero.imageUrls?.map((img) => ({
                      url: img.url,
                      altText: img.altText,
                  })) || []) as HeroImage[],
                  tags: (apiData.hero.tags?.map((tag) => ({
                      label: tag.label,
                      description: tag.description,
                  })) || []) as HeroTag[],
              }
            : undefined,

        // Hero Banner
        heroBanner: apiData.heroBanner
            ? {
                  title: apiData.heroBanner.title,
                  subtitle: apiData.heroBanner.subtitle,
                  highlightLabel: apiData.heroBanner.highlightLabel,
                  ctaText: apiData.heroBanner.ctaText,
                  redirectionUrl: apiData.heroBanner.redirectionUrl,
              }
            : undefined,

        // Location & Contact
        location: apiData.location
            ? {
                  holidayInfo: apiData.location.holidayInfo,
              }
            : undefined,

        // Geo Location
        geoLocation: apiData.geoLocation
            ? {
                  // API returns coordinates as [longitude, latitude]
                  latitude: apiData.geoLocation.coordinates?.[1],
                  longitude: apiData.geoLocation.coordinates?.[0],
                  validity: apiData.geoLocation.validity
                      ? new Date(apiData.geoLocation.validity)
                      : undefined,
              }
            : undefined,

        // Gallery
        gallery: apiData.gallery
            ? {
                  imageUrls: (apiData.gallery.imageUrls?.map((img) => ({
                      url: img.url,
                      altText: img.altText,
                  })) || []) as GalleryImage[],
              }
            : undefined,

        // Deals
        deals: apiData.deals
            ? {
                  items: (apiData.deals.items?.map((deal) => ({
                      giftItem: deal.giftItem,
                      limitedOffer: deal.limitedOffer,
                      spendAmount: deal.spendAmount,
                      validity: deal.validity ? new Date(deal.validity) : undefined,
                  })) || []) as DealItem[],
              }
            : undefined,

        // Social Media
        social: apiData.social
            ? {
                  facebook: apiData.social.facebook
                      ? {
                            username: apiData.social.facebook.username,
                            followers: apiData.social.facebook.followers,
                        }
                      : undefined,
                  instagram: apiData.social.instagram
                      ? {
                            username: apiData.social.instagram.username,
                            followers: apiData.social.instagram.followers,
                        }
                      : undefined,
              }
            : undefined,

        // Footer
        footer: apiData.footer
            ? {
                  licenseNumber: apiData.footer.licenseNumber,
                  minimumLegalDrinkingAge: apiData.footer.minimumLegalDrinkingAge,
              }
            : undefined,

        // Modal
        modal: apiData.modal
            ? {
                  headerBadge: apiData.modal.headerBadge,
                  headerTitle: apiData.modal.headerTitle,
                  headerSubtitle: apiData.modal.headerSubtitle,
                  primaryButtonText: apiData.modal.primaryButtonText,
                  primaryButtonInfo: apiData.modal.primaryButtonInfo,
                  footerNote: apiData.modal.footerNote,
              }
            : undefined,

        // Store Operations Config (phone, whatsapp, timings come at root level from API)
        storeOperationsConfig:
            apiData.phone || apiData.whatsappNumber || apiData.timings
                ? {
                      timings: (apiData.timings?.map((t) => ({
                          day: t.day,
                          openingTime: new Date(t.openingTime),
                          closingTime: new Date(t.closingTime),
                      })) || []) as TimingSlot[],
                      phone: apiData.phone,
                      whatsappNumber: apiData.whatsappNumber,
                      location: apiData.storeOperationsConfig?.location ?? undefined,
                  }
                : undefined,

        // Store Brand Operations Config
        storeBrandOperationsConfig: {
            phone: apiData.phone || '',
            whatsappNumber: apiData.whatsappNumber || '',
        },
    };

    return formData;
};
